"use client";

/**
 * Free first aid needs calculator (/free/first-aid-needs-calculator, SEO plan
 * Workstream E row 6).
 *
 * Runs entirely in the browser: the answers live in React state only.
 * Nothing is fetched, stored or put in the URL, and the only analytics are
 * the fixed values in ./analytics.ts. The logic and every word of the result
 * are lib/marketing/first-aid-calculator/calculator.ts, pinned path by path in
 * __tests__/lib/marketing/first-aid-calculator/calculator.test.ts.
 */

import { useId, useRef, useState } from "react";
import { usePostHog } from "posthog-js/react";
import {
  BLOCKS,
  HIGHER_HAZARD_LINE,
  NOT_LEGAL_ADVICE,
  SCOPE_LINE,
  STARTING_POINT_LINE,
  assessFirstAid,
  isComplete,
  questionsFor,
  type FirstAidAnswers,
} from "@/lib/marketing/first-aid-calculator/calculator";
import { PRIVATE_REGION_CLASS, SECONDARY_BUTTON_CLASS } from "@/components/marketing/exposure-calculators/fields";
import { FirstAidBlock } from "./block-view";
import { trackFirstAidCalculator } from "./analytics";

type Answers = Partial<FirstAidAnswers>;

export function FirstAidCalculator() {
  const posthog = usePostHog();
  const baseId = useId();
  const usedReported = useRef(false);
  const completedReported = useRef(false);
  const [answers, setAnswers] = useState<Answers>({});

  const questions = questionsFor(answers);
  const complete = isComplete(answers);
  const result = complete ? assessFirstAid(answers) : null;

  function answer(id: keyof FirstAidAnswers, value: string) {
    if (!usedReported.current) {
      usedReported.current = true;
      trackFirstAidCalculator(posthog, { event: "first_aid_calculator_used" });
    }
    const next = { ...answers, [id]: value } as Answers;
    // The employer-only questions do not apply to a self-employed person on
    // their own, so a switch drops their answers and they are asked afresh.
    if (id === "employs" && answers.employs !== value) {
      delete next.size;
      delete next.shifts;
      delete next.lone;
    }
    setAnswers(next);
    if (!completedReported.current && isComplete(next)) {
      completedReported.current = true;
      trackFirstAidCalculator(posthog, { event: "first_aid_calculator_completed" });
    }
  }

  return (
    <section
      aria-labelledby={`${baseId}-heading`}
      data-testid="first-aid-calculator"
      data-clarity-mask="true"
      className={`${PRIVATE_REGION_CLASS} rounded-2xl border border-gray-200 bg-gray-50 p-5 sm:p-6`}
    >
      <h2 id={`${baseId}-heading`} className="text-2xl font-bold text-gray-900">
        Work out your first-aid needs
      </h2>
      <p className="mt-2 text-sm leading-relaxed text-gray-700">
        Answer the questions about your site. Your answers stay in your browser. Nothing you choose is sent to us.
      </p>

      <div className="mt-5 space-y-5 rounded-xl border border-gray-200 bg-white p-4">
        {questions.map((question) => {
          const hintId = question.hint ? `${baseId}-${question.id}-hint` : undefined;
          return (
            <fieldset key={question.id} data-testid={`first-aid-question-${question.id}`} aria-describedby={hintId}>
              <legend className="text-sm font-semibold text-gray-900">{question.label}</legend>
              {question.hint ? (
                <p id={hintId} className="mt-1 text-sm text-gray-600">
                  {question.hint}
                </p>
              ) : null}
              <div className="mt-2 space-y-2">
                {question.options.map((option) => (
                  <label key={option.value} className="flex items-start gap-3 text-sm text-gray-800">
                    <input
                      type="radio"
                      name={`${baseId}-${question.id}`}
                      value={option.value}
                      checked={answers[question.id] === option.value}
                      onChange={() => answer(question.id, option.value)}
                      className="mt-0.5 h-5 w-5 shrink-0 border-gray-300 text-accent focus:ring-accent"
                    />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
            </fieldset>
          );
        })}
      </div>

      <div className="mt-4">
        <button type="button" onClick={() => setAnswers({})} className={SECONDARY_BUTTON_CLASS}>
          Start again
        </button>
      </div>

      <div aria-live="polite" data-testid="first-aid-result" className="mt-5 rounded-xl border border-gray-200 bg-white p-4">
        {result === null ? (
          <p className="text-sm text-gray-600">Answer every question to see HSE&rsquo;s suggested provision.</p>
        ) : (
          <>
            <p className="text-sm text-gray-600">HSE&rsquo;s suggested starting point on these answers</p>
            <p data-testid="first-aid-summary" className="mt-1 text-2xl font-extrabold text-gray-900">
              {result.summary}
            </p>
            <p data-testid="first-aid-starting-point" className="mt-3 text-sm font-semibold leading-relaxed text-gray-900">
              {STARTING_POINT_LINE}
            </p>
            {answers.employs === "yes" ? (
              <p className="mt-1 text-sm leading-relaxed text-gray-700">{HIGHER_HAZARD_LINE}</p>
            ) : null}
            <div className="mt-4 space-y-5">
              {result.blocks.map((id) => (
                <FirstAidBlock key={id} block={BLOCKS[id]} />
              ))}
            </div>
            <p data-testid="first-aid-scope" className="mt-5 text-sm leading-relaxed text-gray-700">
              {SCOPE_LINE} {NOT_LEGAL_ADVICE}
            </p>
          </>
        )}
      </div>
    </section>
  );
}
